// ── Public judge library (EX-PUB01) ─────────────────────────────────────────
// Wire shapes for the four public routes under `/v1/judge/*` —
// `docs/07_API_CONTRACT.md` "Public judge", `docs/27_JUDGE_AND_EXAM_SPEC.md`
// §10.1. The verdict vocabulary is the exam workspace's (`docs/27` §7.1); it is
// re-exported from `features/quiz/coding-task-api` rather than restated so the
// two surfaces cannot drift.

import type { ProblemFileIo } from "@/features/judge/lib/file-io";
import type { FunctionSignature, FunctionStarters } from "@/features/judge/lib/function-signature";
import type {
  CodingTaskRunResult,
  JudgeVerdict,
  StudentTestResult,
} from "@/features/quiz/coding-task-api";

export const PROBLEM_DIFFICULTIES = ["easy", "medium", "hard"] as const;
export type ProblemDifficulty = (typeof PROBLEM_DIFFICULTIES)[number];

export const SOLVED_STATES = ["solved", "attempted", "unsolved"] as const;
export type SolvedState = (typeof SOLVED_STATES)[number];

/**
 * What `solved=` accepts (`EX-PUB21`): the states, plus `untried`, "never
 * submitted". `unsolved` is "not solved", which includes a problem you tried.
 */
export type SolvedFilter = SolvedState | "untried";

/** `EX-PUB21`: the round a practice problem came from, and its letter there. */
export type JudgeProblemSource = {
  slug: string;
  title_en: string;
  title_bn: string | null;
  letter: string;
};

/**
 * `EX-PRT01`, `docs/29` §7.7: the rating from the problem's latest rated round
 * (a contestant rated this solves it about half the time) and that round,
 * `null` for a `private` one.
 */
export type JudgeProblemRating = {
  value: number;
  round: { slug: string; title_en: string; title_bn: string | null } | null;
};

/**
 * `EX-PRT01`: the library's rating filter, inclusive bounds on the scale the
 * server writes (multiples of 100, 800 to 3500). The bands' own edges.
 */
export const PROBLEM_RATING_RANGES = [
  { key: "under1200", max: 1199 },
  { key: "1200to1599", min: 1200, max: 1599 },
  { key: "1600to1999", min: 1600, max: 1999 },
  { key: "2000to2399", min: 2000, max: 2399 },
  { key: "from2400", min: 2400 },
] as const satisfies ReadonlyArray<{ key: string; min?: number; max?: number }>;
export type ProblemRatingRangeKey = (typeof PROBLEM_RATING_RANGES)[number]["key"];

export type JudgeProblemListItem = {
  id: string;
  slug: string;
  title_en: string;
  title_bn: string | null;
  difficulty: ProblemDifficulty;
  tags: string[];
  /** `null` when browsing signed out — there is nobody it could describe. */
  solvedState: SolvedState | null;
  /** `EX-PUB21`. Optional: an older API sends neither field. */
  source?: JudgeProblemSource | null;
  /** `EX-PUB21`, decision 1: practice solvers, `null` below ten. */
  solvers?: number | null;
  /** `EX-PLR01`: practice triers, `null` below ten. Optional for an older API. */
  attempters?: number | null;
  /** `EX-PLR01`: judged practice programs, `null` whenever `attempters` is. */
  submissions?: number | null;
  /** `EX-RTW03`: present only on a run-twice problem; `EX-IAC01` adds `interactive`. */
  kind?: "run_twice" | "interactive";
  /** `EX-FNC05`: present only on a function problem; absent is a whole program. */
  answer?: "function";
  /** `EX-PRT01`: `null` until a rated round measured it. Optional for an older API. */
  rating?: JudgeProblemRating | null;
};

/** `EX-RTW03`: the library's Kind filter; `standard` also matches a problem written before kinds. */
export const PROBLEM_KIND_FILTERS = ["standard", "run_twice", "interactive"] as const;
export type ProblemKindFilter = (typeof PROBLEM_KIND_FILTERS)[number];

/** `EX-FNC05`: the library's Answer filter; `program` is every problem written before Wave 36. */
export const PROBLEM_ANSWER_FILTERS = ["function", "program"] as const;
export type ProblemAnswerFilter = (typeof PROBLEM_ANSWER_FILTERS)[number];

export type JudgeProblemListParams = {
  page?: number;
  limit?: number;
  search?: string;
  difficulty?: ProblemDifficulty;
  tag?: string;
  /** Requires a signed-in caller; the server refuses it for a stranger. */
  solved?: SolvedFilter;
  /** `EX-TAG01`: `solved` is "Most solved"; the server's default is newest first. */
  sort?: ProblemListSort;
  /** `EX-RTW03`. */
  kind?: ProblemKindFilter;
  /** `EX-FNC05`. */
  answer?: ProblemAnswerFilter;
  /** `EX-PRT01`: the rating range, inclusive. */
  ratingMin?: number;
  ratingMax?: number;
};

/** `EX-PRT01` adds `rating` (easiest first) and `rating_desc` (hardest first). */
export const PROBLEM_LIST_SORTS = ["newest", "solved", "rating", "rating_desc"] as const;
export type ProblemListSort = (typeof PROBLEM_LIST_SORTS)[number];

export type DifficultyCounts = Record<ProblemDifficulty, number>;

/** `EX-TAG01` (ADR-0087): a tag in URL form and its public problems. */
export type JudgeTopic = {
  slug: string;
  problems: number;
  byDifficulty: DifficultyCounts;
  /** Indexed from five public problems; below that the page is `noindex`. */
  indexable: boolean;
  updatedAt: string;
};

/** `GET /v1/judge/problem-tags` since `EX-TAG01`. */
export type JudgeTopics = {
  items: JudgeTopic[];
  total: number;
  /** `/judge/problems` is indexed from twenty public problems. */
  libraryIndexable: boolean;
};

/** `EX-PUB21`: the signed-in reader's own practice progress. */
export type JudgePracticeProgress = {
  solved: DifficultyCounts;
  attempted: number;
  lastAttempted: { slug: string; title_en: string; title_bn: string | null } | null;
};

export type JudgeProblemListResponse = {
  problems: JudgeProblemListItem[];
  page: number;
  limit: number;
  total: number;
  /** `EX-PUB21`: per difficulty under every filter but the difficulty. */
  facets?: { difficulty: DifficultyCounts };
  /** `EX-PUB21`: the whole public library per difficulty. */
  library?: DifficultyCounts;
  /** `EX-PUB21`: `null` signed out. */
  viewer?: JudgePracticeProgress | null;
};

export type JudgeProblemSample = {
  input: string;
  output: string;
  explanation_en: string | null;
  explanation_bn: string | null;
  /** `EX-RTW01`: a run-twice sample's example middle lines, when its setter filled them in. */
  firstOutput?: string;
  secondInput?: string;
  /** `EX-IAC01`: an interactive sample's conversation; `input` and `output` are then empty. */
  interaction?: { from: "judge" | "program"; text: string }[];
};

export type JudgeProblemView = {
  id: string;
  slug: string;
  title_en: string;
  title_bn: string | null;
  statement_en: string;
  statement_bn: string | null;
  inputFormat_en: string | null;
  inputFormat_bn: string | null;
  outputFormat_en: string | null;
  outputFormat_bn: string | null;
  constraints_en: string | null;
  constraints_bn: string | null;
  notes_en: string | null;
  notes_bn: string | null;
  samples: JudgeProblemSample[];
  limits: { timeMs: number; memoryKb: number };
  difficulty: ProblemDifficulty;
  /** `EX-PRT01`: the list row's field. Optional for an older API. */
  rating?: JudgeProblemRating | null;
  tags: string[];
  solvedState: SolvedState | null;
  /** `EX-RTW01`: present only on a run-twice problem; `EX-IAC01` adds `interactive`. */
  kind?: "run_twice" | "interactive";
  /** `EX-RTW05`: present only when the program reads or writes a file. */
  fileIo?: ProblemFileIo;
  /** `EX-FNC03`: a function problem's signature and the five starters; both absent on a whole program. */
  signature?: FunctionSignature;
  starters?: FunctionStarters;
  /**
   * `EX-CRD01` (ADR-0098): who wrote the problem, printed under its title.
   * `null` for a problem made from the admin panel (its author holds `admin`
   * or `super_admin`), an account that is gone, or an issued contest login.
   * Optional for an older API, which reads as nobody named.
   */
  author?: JudgeProblemAuthor | null;
  /**
   * `EX-ARC01`, `docs/29` §6.4: the round this problem came from, when it
   * reached the archive that way. `null` for every problem published directly.
   * The person is {@link author} since `EX-CRD01`, so the page names them once.
   */
  credit: JudgeProblemCredit | null;
  /** `EX-PUB21`, decision 1: practice solvers, `null` below ten. Optional for an older API. */
  solvers?: number | null;
  /**
   * `EX-EDT01`: whether an editorial exists in either language, so the page
   * offers the tab. Never the text, which is its own read. Optional for an
   * older API, which reads as "no editorial".
   */
  hasEditorial?: boolean;
};

/** `EX-STA01` (ADR-0085): one row of a problem's ranked lists, by the identity rule. */
export type JudgeProblemStatsRow = {
  /** `EX-SRC01`: present only for a reader who solved the problem; opens the program. */
  submissionId?: string;
  name: string;
  username: string | null;
  avatarImageId: string | null;
  languageKey: string;
  timeMs: number | null;
  memoryKb: number | null;
  sourceBytes: number;
  submittedAt: string;
};

/**
 * `EX-STA01` (ADR-0085): `GET /v1/judge/problems/:slug/stats`. Practice only;
 * every figure `null` and both breakdowns empty below ten judged submissions,
 * the solver count `null` below ten solvers.
 */
export type JudgeProblemStats = {
  judged: number | null;
  accepted: number | null;
  attempters: number | null;
  solvers: number | null;
  acceptanceRate: number | null;
  verdicts: { verdict: string; count: number }[];
  languages: { languageKey: string; submissions: number; accepted: number }[];
  fastest: JudgeProblemStatsRow[];
  lightest: JudgeProblemStatsRow[];
  shortest: JudgeProblemStatsRow[];
};

/** `EX-SRC01` (ADR-0082): the Submissions tab's query. */
export type JudgeProblemSubmissionsParams = {
  page: number;
  limit: number;
  verdict?: string;
  language?: string;
  mine?: boolean;
};

/** `EX-SRC01`: one row of a problem's public submissions list; never a program. */
export type JudgeProblemSubmissionRow = {
  submissionId: string;
  name: string;
  username: string | null;
  avatarImageId: string | null;
  languageKey: string;
  languageVersion: string | null;
  verdict: JudgeVerdict | null;
  timeMs: number | null;
  memoryKb: number | null;
  sourceBytes: number;
  submittedAt: string;
  own: boolean;
  canRead: boolean;
};

export type JudgeProblemSubmissionsResponse = {
  items: JudgeProblemSubmissionRow[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  viewer: { signedIn: boolean; solved: boolean };
};

/** `EX-SRC01`: one program, as the gate lets it out. */
export type JudgeSubmissionSource = Omit<JudgeProblemSubmissionRow, "sourceBytes" | "canRead"> & {
  sourceCode: string;
};

/** `EX-EDT01` (ADR-0085): `GET /v1/judge/problems/:slug/editorial`. */
export type JudgeProblemEditorial = {
  /**
   * `"live_contest"` while a round that has not ended mounts the problem;
   * `"virtual_round"` (`EX-VRT01`) while the reader's own virtual try at a
   * round mounting it runs.
   */
  withheld: "live_contest" | "virtual_round" | null;
  editorial: { editorial_en: string | null; editorial_bn: string | null } | null;
  /** `EX-VRT01`: with `virtual_round`, the round and when the try ends. Optional on the wire. */
  virtualRound?: { slug: string; title_en: string; opensAt: string };
};

export type JudgeProblemCredit = {
  /**
   * `null` when the round is private: the rule the profile's credit already
   * follows (an API before `EX-CRD01` could still send the credit then).
   * `letter` since `EX-PUB21`, optional for an older API.
   */
  round: { slug: string; title_en: string; title_bn: string | null; letter?: string } | null;
};

/**
 * `EX-CRD01`: the problem's author by the identity rule, name, username and
 * avatar, never a user id; `rating` is today's, for the band's colour, and
 * `null` for somebody no rated round has moved.
 */
export type JudgeProblemAuthor = {
  name: string;
  username: string | null;
  usernameDisplay: string | null;
  avatarImageId: string | null;
  rating: number | null;
};

// ── The graded runtime catalogue (`GET /v1/judge/runtimes`) ─────────────────
// The same shape `/v1/judge-platform/runtimes` serves the exam builder — the
// runner's own enabled table, never the Playground's bundled catalogue. The two
// catalogues deliberately disagree (`docs/24` §3.1) and no picker may merge them.

export type JudgeRuntimeVersion = {
  id: string;
  label: string;
  toolchain: string;
  isDefault: boolean;
};

export type JudgeRuntimeLanguage = {
  id: string;
  name: string;
  defaultVersionId: null;
  versions: JudgeRuntimeVersion[];
  allowedCompilerOptions: readonly string[];
};

export type JudgeRuntimeCatalogue = {
  engine: "runner";
  readAt: string;
  live: boolean;
  languages: JudgeRuntimeLanguage[];
};

// ── Practice submissions ────────────────────────────────────────────────────

export type PracticeSubmitInput = {
  problemId: string;
  languageKey: string;
  languageVersion: string;
  sourceCode: string;
};

/** POST /v1/judge/submissions (202) — the row is queued; the verdict arrives by polling. */
export type PracticeSubmitResult = {
  submissionId: string;
  status: "queued" | "running" | "completed";
  queuePosition: number;
};

/** The practice read is always policy `full` — the problem is public, so nothing is withheld. */
export type PracticeSubmissionResult = {
  status: "queued" | "running" | "completed";
  verdict?: JudgeVerdict;
  score: number;
  maxScore: number;
  timeMs?: number;
  memoryKb?: number;
  compileOutput?: string;
  testResults?: StudentTestResult[];
  hidden?: { passedCount: number; totalCount: number };
};

/** GET /v1/judge/submissions/:id */
export type PracticeSubmissionDetail = {
  submissionId: string;
  status: "queued" | "running" | "completed";
  queuePosition: number | null;
  languageKey: string;
  languageVersion: string;
  sourceCode: string;
  createdAt: string;
  result: PracticeSubmissionResult;
};

export type SubmissionHistoryProblemRef = {
  id: string;
  slug: string;
  title_en: string;
  title_bn: string | null;
  difficulty: ProblemDifficulty;
};

export type SubmissionHistoryItem = {
  id: string;
  /** `null` when the problem row no longer exists. */
  problem: SubmissionHistoryProblemRef | null;
  languageKey: string;
  languageVersion: string;
  status: "queued" | "running" | "completed";
  verdict: JudgeVerdict | null;
  score: number;
  maxScore: number;
  timeMs: number | null;
  memoryKb: number | null;
  createdAt: string;
};

export type SubmissionHistoryParams = {
  page?: number;
  limit?: number;
  problemId?: string;
};

export type SubmissionHistoryResponse = {
  submissions: SubmissionHistoryItem[];
  page: number;
  limit: number;
  total: number;
};

// ── Sample runs (`POST /v1/judge/problems/:slug/run`, `EX-PUB12`) ──────────
// Feedback, not a mark: no submission id, no queue position - nothing here
// persists. `docs/27` §9.7.8's seam, exposed publicly instead of session-scoped.

export type RunSampleInput = {
  languageKey: string;
  languageVersion: string;
  sourceCode: string;
  /**
   * `EX-EDR02`: one custom case instead of the samples, at most 16 KiB. Absent
   * runs the samples; present, even empty, runs this input and compares nothing.
   * The contest run takes the same body.
   */
  stdin?: string;
};

/**
 * The exam run's shape, `customInput` included: since `EX-EDR02` the practice
 * route, the contest route and the course exercise all answer with it, so it
 * is `CodingTaskRunResult` itself rather than a copy that could drift.
 */
export type SampleRunResult = CodingTaskRunResult;

/** A submission still awaiting a verdict — drives the workspace's poll. */
export function isPracticeSubmissionPending(
  submission: Pick<PracticeSubmissionDetail, "status">,
): boolean {
  return submission.status !== "completed";
}

// ── Practice hints (`EX-HNT01`, `docs/27` §10.1.7) ────────────────────────────
// After a wrong verdict: three steps, the setter's own first and free, AI's
// after them at one credit each. While a contest or an exam mounting the
// problem is in its window, `locked` is set and no step carries text.

export type PracticeHintKind = "idea" | "approach" | "edge";
export type PracticeHintSource = "setter" | "ai";

export type PracticeHintStep = {
  n: number;
  kind: PracticeHintKind;
  source: PracticeHintSource;
  shown: boolean;
  /** Only on a shown step; `null` when the setter has since removed it. */
  text: string | null;
  /** What showing it costs: 0 for the setter's own, else AI credits. */
  cost: number;
};

export type PracticeHints = {
  /** `EX-VRT01`: `virtual_running` while the reader's own virtual try with this problem runs. */
  locked: { reason: "contest_running" | "exam_live" | "virtual_running" } | null;
  aiCost: number;
  steps: PracticeHintStep[];
};

export type RevealPracticeHintInput = {
  step: number;
  submissionId: string;
  locale: "en" | "bn";
  /** Fresh `phnt_<UUIDv4>` per deliberate click on an AI step; reused only for a retry. */
  idempotencyKey?: string;
};

export type RevealPracticeHintResult = {
  hints: PracticeHints;
  /** False when the step was already shown (a double click, a replay). */
  fresh: boolean;
};
